/**
 * The renderer core: scene graph, the frame, and everything that decides what
 * is on screen.
 *
 * It owns the camera, the level's batched geometry, the light rig and the
 * flashlight, and it drives — but does not implement — the texture set, the
 * material set, the post-processing chain and the entity/item visuals. Those
 * come in through `render/api.ts` so they can be built independently.
 *
 * The look is carried by fog rather than by draw distance: `scene.fog` is
 * brought in to agree with `TUNING.render.cullDistance` so that a chunk is
 * already fully fogged out by the time it is switched off, and nothing ever
 * pops into view.
 */

import * as THREE from 'three';
import { TUNING } from '../core/config';
import { clamp, clamp01, damp, remap, renderYaw } from '../core/math';
import type {
  EntityKind,
  EntityState,
  GameContext,
  GeneratedLevel,
  Grid,
  ItemKind,
  ItemSpot,
  LevelSpec,
  PlayerState,
  RenderAPI,
  RenderSettings,
  Vec2,
} from '../core/types';
import type { MaterialSet, TextureSet, EntityView, ItemView, PropView } from './api';
import { createEntityView } from './entityviews';
import { createItemView } from './itemviews';
import { createPropView } from './propviews';
import { makeFlashlight, type Flashlight } from './flashlight';
import { makeLightRig, type LightRig } from './lights';
import { makeMaterialSet } from './materials';
import { buildLevelGeometry, type LevelGeometry } from './meshbuilder';
import { makePostFx, type DrainablePostFx } from './postfx';
import { makeTextureSet } from './textures';
// The headset. Inert on every machine that is not wearing one: see `xr.ts`.
import { makeXR, type XRRuntime } from './xr';
import { xrQuality } from './xrquality';

interface EntitySlot {
  kind: EntityKind;
  view: EntityView;
}

interface ItemSlot {
  kind: ItemKind;
  view: ItemView;
}

/** Device pixel ratio ceiling per quality tier. */
const PIXEL_RATIO_CAP: Record<RenderSettings['quality'], number> = { low: 1, medium: 1.5, high: 2 };

/**
 * three tone-maps geometry in the fragment shader but writes the clear colour
 * straight through, so a surface that has faded entirely into the fog does not
 * match the background behind it. Pre-tone-map the background by hand to close
 * the seam. Mirrors `ACESFilmicToneMapping` in three's tonemapping shader.
 */
function acesFilmic(color: THREE.Color, exposure: number, out: THREE.Color): THREE.Color {
  const k = exposure / 0.6;
  const r = color.r * k;
  const g = color.g * k;
  const b = color.b * k;
  const ir = 0.59719 * r + 0.35458 * g + 0.04823 * b;
  const ig = 0.076 * r + 0.90834 * g + 0.01566 * b;
  const ib = 0.0284 * r + 0.13383 * g + 0.83777 * b;
  const x = fitRRT(ir);
  const y = fitRRT(ig);
  const z = fitRRT(ib);
  return out.setRGB(
    clamp01(1.60475 * x - 0.53108 * y - 0.07367 * z),
    clamp01(-0.10208 * x + 1.10813 * y - 0.00605 * z),
    clamp01(-0.00327 * x - 0.07276 * y + 1.07602 * z)
  );
}

function fitRRT(v: number): number {
  return (v * (v + 0.0245786) - 0.000090537) / (v * (0.983729 * v + 0.432951) + 0.238081);
}

class Renderer implements GameRenderer {
  readonly scene = new THREE.Scene();
  readonly camera: THREE.PerspectiveCamera;
  readonly renderer: THREE.WebGLRenderer;
  /**
   * The immersive session. Built at boot on every machine and inert on almost
   * all of them — it asks the browser one question and, unless the answer is
   * yes, never touches the DOM, the scene or a frame again.
   */
  readonly xr: XRRuntime;
  /**
   * True only between `sessionstart` and `sessionend`. Every XR branch below
   * hangs off this one boolean, which is the whole cost the flat game pays.
   */
  private xrOn = false;
  /** Camera position in world space. Only read while `xrOn`; see `cullChunks`. */
  private readonly camWorld = new THREE.Vector3();
  /**
   * A world-space stand-in for the camera, built on the first session and never
   * on a flat machine. See `aimCamera`.
   */
  private xrAimCamera: THREE.PerspectiveCamera | null = null;

  /** A private copy: the settings system hands out a new object on every edit. */
  private readonly settings: RenderSettings;
  private readonly levelGroup = new THREE.Group();
  private readonly entityGroup = new THREE.Group();
  private readonly itemGroup = new THREE.Group();
  private readonly propGroup = new THREE.Group();
  private readonly propViews: PropView[] = [];
  /**
   * Where each prop stands, and whether it is tall enough to be a landmark.
   * Parallel to `propViews`; see `cullProps`.
   */
  private readonly propAnchors: Array<{ x: number; z: number; landmark: boolean }> = [];
  private readonly ambient = new THREE.AmbientLight(0xffffff, 0);
  private readonly bounce = new THREE.HemisphereLight(0xffffff, 0xffffff, 0);
  private readonly fog = new THREE.Fog(0x000000, 1, 40);
  private readonly background = new THREE.Color(0x000000);
  private readonly flashlight: Flashlight;

  private textures: TextureSet | null = null;
  private materials: MaterialSet | null = null;
  private geometry: LevelGeometry | null = null;
  private lightRig: LightRig | null = null;
  private postfx: DrainablePostFx | null = null;
  private postfxFor = '';
  /** Kept only so `lightLevelAt` can tell whether a wall is in the way. */
  private grid: Grid | null = null;
  /** This level's cull boundary in metres. See `LevelSpec.viewDistance`. */
  private cullDistance = TUNING.render.cullDistance;
  /**
   * The current floor's spec, kept only so the cull boundary and the fog can be
   * re-derived without a level rebuild — see `applyViewDistance`.
   */
  private levelSpec: LevelSpec | null = null;

  private readonly entityViews = new Map<number, EntitySlot>();
  private readonly itemViews = new Map<number, ItemSlot>();
  private entityList: readonly EntityState[] = [];
  private itemList: readonly ItemSpot[] = [];
  /** Reused by the view reaping passes so a frame allocates nothing. */
  private readonly liveIds = new Set<number>();

  private readonly frustum = new THREE.Frustum();
  private readonly projScreen = new THREE.Matrix4();

  private width = 1;
  private height = 1;
  private pixelRatio = 1;
  private time = 0;
  private roll = 0;
  private rollTarget = 0;
  private sanity = 0;
  private damage = 0;
  private ambientFloor = 0;
  /** The level's authored ambient, kept so the light drain can pull it down. */
  private ambientBase = 0;
  private bounceBase = 0;
  /**
   * Smoothed light drain at the player. The rig's value is already continuous,
   * but this lags it so that a drainer being killed or recycled hands the room
   * back over about a second rather than switching it on.
   */
  private drain = 0;
  private playerX = 0;
  private playerZ = 0;
  private playerYaw = 0;

  private appliedFov: number;
  private appliedPostFx: boolean;
  private appliedResolutionScale: number;

  constructor(canvas: HTMLCanvasElement, settings: RenderSettings) {
    this.settings = { ...settings };
    this.renderer = new THREE.WebGLRenderer({
      canvas,
      antialias: settings.quality === 'high',
      powerPreference: 'high-performance',
      stencil: false,
    });
    this.renderer.shadowMap.enabled = false;
    // index.html carries no stylesheet, so the canvas is sized inline by resize().
    canvas.style.display = 'block';
    // Fluorescent panels blow out badly without a shoulder on the response.
    this.renderer.toneMapping = THREE.ACESFilmicToneMapping;
    this.renderer.toneMappingExposure = TUNING.render.toneMappingExposure;

    this.camera = new THREE.PerspectiveCamera(settings.fov || TUNING.render.defaultFov, 1, TUNING.render.near, TUNING.render.far);
    this.camera.rotation.order = 'YXZ';

    this.scene.background = this.background;
    this.scene.fog = this.fog;
    this.scene.add(this.levelGroup, this.entityGroup, this.itemGroup, this.propGroup, this.ambient, this.bounce);
    // The camera lives in the scene so that lights parented to it are collected
    // during the scene traversal; a detached camera's children are invisible.
    this.scene.add(this.camera);

    this.flashlight = makeFlashlight();
    this.camera.add(this.flashlight.root);

    this.appliedFov = this.camera.fov;
    this.appliedPostFx = settings.postFx;
    this.appliedResolutionScale = settings.resolutionScale;

    // Last, because it wants the flashlight to exist: in a session the torch
    // moves off the head and into a hand.
    this.xr = makeXR({
      renderer: this.renderer,
      scene: this.scene,
      camera: this.camera,
      flashlight: this.flashlight.root,
      quality: () => this.settings.quality,
      postGrade: () => this.settings.postFx,
      onPresenting: this.onPresenting,
    });
  }

  /**
   * A session starting or ending.
   *
   * On the way in, the headset's quality tier is applied — a copy of the
   * player's settings with the draw distance, the light budget and head bob cut
   * for stereo. On the way out, everything that has to be *put back* is put
   * back: three overwrites the camera's field of view and projection from the
   * headset's own, and restores the canvas size and pixel ratio itself but not
   * the aspect ratio, so the flat view comes back wrong without this.
   */
  private onPresenting = (on: boolean): void => {
    this.xrOn = on;
    if (on) {
      this.setSettings(xrQuality.enter(this.renderer, this.settings));
      return;
    }
    const restore = xrQuality.exit(this.renderer);
    this.camera.fov = this.appliedFov;
    if (restore) this.setSettings(restore);
    this.resize(this.width, this.height);
  };

  /* ---------------------------------------------------------------- *
   * Level lifecycle
   * ---------------------------------------------------------------- */

  buildLevel(level: GeneratedLevel): void {
    this.clearLevel();
    const spec = level.spec;
    this.grid = level.grid;
    this.textures = makeTextureSet(spec);
    this.materials = makeMaterialSet(spec, this.textures);
    this.geometry = buildLevelGeometry(level, this.materials);
    for (const chunk of this.geometry.chunks) this.levelGroup.add(chunk.group);

    this.lightRig = makeLightRig(level, this.materials, this.geometry, this.scene, this.dynamicLightBudget());
    this.buildProps(level);

    this.levelSpec = spec;
    this.applyViewDistance();
    // Distance should read as *dark*, not as haze. The palette's fog colour is
    // close to the level's own lit tone, so at the old 44m cull it doubled as a
    // bright wall you never quite reached. Pulled well down toward black, the
    // far end of a long sightline goes to shadow instead — which is both what
    // the place looks like and what makes the extra draw distance worth having.
    this.fog.color.setHex(spec.palette.fog).multiplyScalar(TUNING.render.fogDarkness);
    acesFilmic(this.fog.color, this.renderer.toneMappingExposure, this.background);

    this.ambient.color.setHex(spec.palette.ambient);
    this.ambient.intensity = spec.ambientIntensity;
    this.bounce.color.setHex(spec.palette.ceiling);
    this.bounce.groundColor.setHex(spec.palette.carpet);
    this.bounce.intensity = spec.ambientIntensity * 0.5;
    this.ambientFloor = clamp01(spec.ambientIntensity * 0.5);
    this.ambientBase = this.ambient.intensity;
    this.bounceBase = this.bounce.intensity;
    this.drain = 0;

    if (!this.postfx || this.postfxFor !== spec.id) {
      this.postfx?.dispose();
      this.postfx = makePostFx(this.renderer, spec);
      this.postfxFor = spec.id;
      this.postfx.setSize(this.width, this.height, this.pixelRatio);
    }
    this.postfx.setEnabled(this.settings.postFx);
    this.postfx.setSanity(this.sanity);
    this.postfx.setDamage(this.damage);
  }

  /**
   * Scenery is static, so it is placed once and then left alone. Views are
   * built per prop but share geometry internally, and the whole lot is parented
   * to one group so teardown is a single traversal.
   */
  private buildProps(level: GeneratedLevel): void {
    for (const spot of level.props) {
      const view = createPropView(spot.kind, level.spec);
      view.place(spot);
      this.propViews.push(view);
      this.propAnchors.push({
        x: spot.pos.x,
        z: spot.pos.z,
        landmark: (spot.footprint?.height ?? 0) >= LANDMARK_HEIGHT,
      });
      this.propGroup.add(view.object);
    }
  }

  clearLevel(): void {
    for (const view of this.propViews) {
      this.propGroup.remove(view.object);
      view.dispose();
    }
    this.propViews.length = 0;
    this.propAnchors.length = 0;

    for (const slot of this.entityViews.values()) {
      this.entityGroup.remove(slot.view.object);
      slot.view.dispose();
    }
    this.entityViews.clear();
    this.entityList = [];
    for (const slot of this.itemViews.values()) {
      this.itemGroup.remove(slot.view.object);
      slot.view.dispose();
    }
    this.itemViews.clear();
    this.itemList = [];

    this.lightRig?.dispose();
    this.lightRig = null;

    if (this.geometry) {
      for (const chunk of this.geometry.chunks) this.levelGroup.remove(chunk.group);
      this.geometry.dispose();
      this.geometry = null;
    }
    this.materials?.dispose();
    this.materials = null;
    this.textures?.dispose();
    this.textures = null;
    this.grid = null;
  }

  /* ---------------------------------------------------------------- *
   * Per-frame state in
   * ---------------------------------------------------------------- */

  syncCamera(player: PlayerState, eye?: { x: number; y: number; z: number }, roll?: number): void {
    // Light and audio queries want where the *body* is, not where the head has
    // swayed to, so these stay on player.pos.
    this.playerX = player.pos.x;
    this.playerZ = player.pos.z;
    this.playerYaw = player.yaw;

    if (this.xrOn) {
      // In a session three owns the camera pose and the game owns the rig it
      // sits in, which `XRPresenter.afterPlayer` has already placed. Writing
      // position or rotation here would be writing over the headset. Head bob
      // is off in there for the same reason it is off in every VR game: the
      // headset already knows where the head is.
      this.xr.trackPlayer(player);
      this.rollTarget = 0;
      return;
    }

    // eyeHeight already carries the vertical bob and the crouch blend; the
    // lateral sway only exists on the controller, so use it when it is handed
    // over and fall back to the body position when it is not.
    const bob = this.settings.headBob && eye ? eye : null;
    this.camera.position.set(bob ? bob.x : player.pos.x, bob ? bob.y : player.eyeHeight, bob ? bob.z : player.pos.z);
    // `renderYaw` is the whole conversion between the game's yaw convention and
    // three.js's. Without it the camera faces the mirror image of the direction
    // the player walks, interacts and is seen in — see core/math.
    this.camera.rotation.set(player.pitch, renderYaw(player.yaw), this.roll, 'YXZ');

    if (!this.settings.headBob) {
      this.rollTarget = 0;
    } else if (roll !== undefined) {
      this.rollTarget = roll;
    } else {
      // No controller handle: recover a lean from the strafe component instead.
      const rx = Math.cos(player.yaw);
      const rz = Math.sin(player.yaw);
      const strafe = player.vel.x * rx + player.vel.z * rz;
      this.rollTarget = -clamp(strafe / TUNING.player.walkSpeed, -1.4, 1.4) * TUNING.player.strafeRoll;
    }
  }

  /**
   * Derive how far this floor can be seen, and fog it to match.
   *
   * Split out of `buildLevel` because it has two callers now. The numbers come
   * from the level's own spec and from `TUNING.render`, and on a flat machine
   * neither of those ever changes between the two — re-deriving is arithmetic
   * on the same inputs and lands on the same numbers, which is why calling it
   * from `setSettings` costs the desktop nothing.
   *
   * It matters in a session. `xrquality.ts` swaps `TUNING.render` for the
   * headset set on the way in and swaps it back on the way out, and both of
   * those arrive here as a `setSettings`. Without this call the *prop* cull —
   * which reads `TUNING.render.cullDistance` live, per frame — would shorten
   * immediately while the chunk boundary and the fog stayed where the last
   * level build left them, and Level 94 would lose its houses out to a fog line
   * that had not moved. With it, the fog closes with them and it reads as
   * weather rather than as a bug.
   *
   * Fog has to be finished before the cull boundary or chunks appear from
   * nowhere; near follows far so short-sighted levels stay claustrophobic.
   * `viewDistanceCap` is `Infinity` on desktop, so `Math.min` returns the
   * level's own number untouched. It exists so a headset session can pull Level
   * 94's 118 m and Level 11's 92 m down to something it can afford to draw
   * twice, without editing either level's spec. See `xrquality.ts`.
   */
  private applyViewDistance(): void {
    const spec = this.levelSpec;
    if (!spec) return;
    this.cullDistance = Math.min(spec.viewDistance ?? TUNING.render.cullDistance, TUNING.render.viewDistanceCap);
    const far = Math.min(spec.fogFar, this.cullDistance * TUNING.render.fogFarScale);
    this.fog.far = far;
    this.fog.near = Math.min(spec.fogNear, far * 0.55);
  }

  /**
   * The settings menu writes through here. `quality` cannot change the WebGL
   * context's antialias flag after construction, but it does move the pixel
   * ratio cap and the dynamic light budget, so it is applied on the next level
   * build and immediately for the resolution.
   */
  setSettings(settings: RenderSettings): void {
    const s = this.settings;
    const qualityChanged = s.quality !== settings.quality;
    s.quality = settings.quality;
    s.fov = settings.fov;
    s.postFx = settings.postFx;
    s.headBob = settings.headBob;
    s.resolutionScale = settings.resolutionScale;

    const fov = s.fov || TUNING.render.defaultFov;
    if (fov !== this.appliedFov) {
      this.appliedFov = fov;
      this.camera.fov = fov;
      this.camera.updateProjectionMatrix();
    }
    if (s.postFx !== this.appliedPostFx) {
      this.appliedPostFx = s.postFx;
      this.postfx?.setEnabled(this.appliedPostFx);
    }
    if (qualityChanged || s.resolutionScale !== this.appliedResolutionScale) {
      this.appliedResolutionScale = s.resolutionScale;
      this.resize(this.width, this.height);
    }
    // Last, and unconditional: entering or leaving a session arrives here, and
    // the draw distance it swapped has to reach the chunks and the fog as well
    // as the props. A no-op on every other call. See `applyViewDistance`.
    this.applyViewDistance();
  }

  syncEntities(entities: readonly EntityState[]): void {
    this.entityList = entities;
    for (const e of entities) {
      const slot = this.entityViews.get(e.id);
      if (slot && slot.kind === e.kind) continue;
      if (slot) {
        this.entityGroup.remove(slot.view.object);
        slot.view.dispose();
      }
      const view = createEntityView(e.kind);
      this.entityViews.set(e.id, { kind: e.kind, view });
      this.entityGroup.add(view.object);
    }
    if (this.entityViews.size === entities.length) return;
    this.liveIds.clear();
    for (const e of entities) this.liveIds.add(e.id);
    for (const [id, slot] of this.entityViews) {
      if (this.liveIds.has(id)) continue;
      this.entityGroup.remove(slot.view.object);
      slot.view.dispose();
      this.entityViews.delete(id);
    }
  }

  syncItems(items: readonly ItemSpot[]): void {
    this.itemList = items;
    for (const it of items) {
      const slot = this.itemViews.get(it.id);
      if (slot && slot.kind === it.kind) continue;
      if (slot) {
        this.itemGroup.remove(slot.view.object);
        slot.view.dispose();
      }
      const view = createItemView(it.kind);
      this.itemViews.set(it.id, { kind: it.kind, view });
      this.itemGroup.add(view.object);
    }
    if (this.itemViews.size === items.length) return;
    this.liveIds.clear();
    for (const it of items) this.liveIds.add(it.id);
    for (const [id, slot] of this.itemViews) {
      if (this.liveIds.has(id)) continue;
      this.itemGroup.remove(slot.view.object);
      slot.view.dispose();
      this.itemViews.delete(id);
    }
  }

  setSanityEffect(v: number): void {
    this.sanity = clamp01(v);
    this.postfx?.setSanity(this.sanity);
  }

  setDamageFlash(v: number): void {
    this.damage = clamp01(v);
    this.postfx?.setDamage(this.damage);
  }

  setFlashlight(on: boolean, charge: number): void {
    this.flashlight.set(on, charge);
    // A torch only appears in the hand once the player has found one; charge is
    // zero until the pickup grants it.
    if (this.xrOn) this.xr.setHolding(on || charge > 0);
  }

  /**
   * The camera, guaranteed to carry a world-space `position` and `quaternion`.
   *
   * `render/remoteplayers.ts` and `render/remotestrike.ts` aim and sort off
   * those two fields directly rather than off `matrixWorld`, which is correct
   * and cheap on a flat frame — the camera is parented at the scene root, so
   * its local transform *is* its world transform, and the renderer has not
   * refreshed matrices by the time they are called.
   *
   * In a session it is not: `camera.parent` is the XR rig, so `position` is an
   * offset inside the play space and `quaternion` is a head rotation with no
   * yaw from the rig in it. Aiming a strike or facing a name label off those
   * would miss by however far the player has walked. So while presenting this
   * hands back a stand-in whose local transform has been decomposed from the
   * real camera's world matrix.
   *
   * On a flat frame this returns the identical object it always did and
   * allocates nothing: the stand-in is only ever constructed inside a session.
   */
  aimCamera(): THREE.Camera {
    if (!this.xrOn) return this.camera;
    const proxy = this.xrAimCamera ?? (this.xrAimCamera = new THREE.PerspectiveCamera());
    this.camera.updateMatrixWorld();
    this.camera.matrixWorld.decompose(proxy.position, proxy.quaternion, proxy.scale);
    return proxy;
  }

  /* ---------------------------------------------------------------- *
   * Frame
   * ---------------------------------------------------------------- */

  update(dt: number, ctx: GameContext): void {
    this.time += dt;
    this.roll = damp(this.roll, this.rollTarget, 9, dt);
    if (this.xrOn) {
      // Writing `camera.rotation.z` in a session would write straight over the
      // head pose three decomposed onto the camera — and a rolled horizon is
      // the single most reliable way to make somebody ill in a headset.
      // `xr.update` also settles the rig's world matrix, which the culling
      // below reads through the camera's parent.
      this.xr.update(dt);
    } else {
      this.camera.rotation.z = this.roll;
    }
    this.camera.updateMatrixWorld();
    this.camera.matrixWorldInverse.copy(this.camera.matrixWorld).invert();

    this.cullChunks();

    // The rig has to see this frame's entities before it decides what the
    // ceiling is doing, and the torch and the post chain have to see the result
    // of that, so this order is load-bearing.
    this.lightRig?.syncDrain(this.entityList);
    this.lightRig?.update(dt, this.time, this.playerX, this.playerZ, ctx);
    this.applyDrain(dt);
    this.flashlight.update(dt, this.time);
    this.materials?.update(dt, this.time, this.lightRig?.localFlicker ?? 1);

    for (const e of this.entityList) {
      this.entityViews.get(e.id)?.view.update(e, dt, this.time, this.camera);
    }
    for (const it of this.itemList) {
      this.itemViews.get(it.id)?.view.update(it, dt, this.time, this.camera);
    }

    this.postfx?.update(dt);
  }

  render(): void {
    // Deliberately not branched on `xrOn`. The post chain knows about sessions
    // — in one it renders the scene straight through and lays a blend-only
    // grade over it per eye — and it is also where the frame-rate governor
    // ticks, so routing around it in a headset would take the governor with it.
    if (this.postfx) this.postfx.render(this.scene, this.camera);
    else this.renderer.render(this.scene, this.camera);
  }

  resize(w: number, h: number): void {
    this.width = Math.max(1, Math.floor(w));
    this.height = Math.max(1, Math.floor(h));
    // While presenting, the drawing buffer is the headset's and three refuses
    // to resize it — the size is recorded above so that leaving the session can
    // put the flat view back. See `onPresenting`.
    if (this.xrOn) return;
    this.camera.aspect = this.width / this.height;
    this.camera.updateProjectionMatrix();

    const cap = PIXEL_RATIO_CAP[this.settings.quality] ?? 2;
    const scale = clamp(this.settings.resolutionScale || 1, 0.35, 1);
    this.pixelRatio = clamp(window.devicePixelRatio || 1, 0.5, cap) * scale;
    this.renderer.setPixelRatio(this.pixelRatio);
    this.renderer.setSize(this.width, this.height, true);
    this.postfx?.setSize(this.width, this.height, this.pixelRatio);
  }

  /* ---------------------------------------------------------------- *
   * Queries
   * ---------------------------------------------------------------- */

  lightLevelAt(pos: Vec2): number {
    if (!this.lightRig) return 0;
    // The fixture term already carries the drain — it is baked into each
    // fixture's `lit` — but the ambient floor does not, and the floor is the
    // larger of the two on the levels lit from nowhere. Without this the
    // picture goes dark while the light model says lit, which would mean the
    // dark costs no sanity and hides the player from nothing. Sampled at `pos`
    // rather than at the player, because an entity thirty metres inside the
    // dark should be blind even when the player is standing in the light.
    const drain = this.lightRig.drainAt(pos.x, pos.z);
    let v = this.ambientFloor * (1 - drain * TUNING.render.lightDrainAmbientDepth) + this.lightRig.levelAt(pos.x, pos.z);

    const beam = this.flashlight.strength;
    if (beam > 0.01) {
      const dx = pos.x - this.playerX;
      const dz = pos.z - this.playerZ;
      const d = Math.sqrt(dx * dx + dz * dz);
      if (d < this.flashlight.range) {
        if (d < 1.2) {
          v += beam * 0.7;
        } else {
          const inv = 1 / d;
          const cosA = dx * inv * Math.sin(this.playerYaw) + dz * inv * -Math.cos(this.playerYaw);
          if (cosA > this.flashlight.cosHalfAngle && !this.blocked(pos.x, pos.z, dx, dz)) {
            const cone = remap(cosA, this.flashlight.cosHalfAngle, 1, 0.3, 1);
            v += beam * cone * (1 - d / this.flashlight.range) * 0.85;
          }
        }
      }
    }
    return clamp01(v);
  }

  dispose(): void {
    this.xr.dispose();
    this.clearLevel();
    this.postfx?.dispose();
    this.postfx = null;
    this.flashlight.dispose();
    this.renderer.dispose();
  }

  /* ---------------------------------------------------------------- *
   * Internals
   * ---------------------------------------------------------------- */

  /**
   * Everything the light drain touches that is not a ceiling fixture.
   *
   * The fixtures carry the effect at range — see `render/lights.ts` — but they
   * cannot carry it up close, because a level's flat ambient is what is still
   * lighting the walls once every tube nearby is out, and on a level lit from
   * nowhere in particular (Level 4) it is most of the light in the room. So the
   * ambient comes down too, keyed off the drain *at the player*: the room you
   * are standing in is the one that goes dark.
   *
   * `lightDrainAmbientDepth` is short of 1 on purpose and is the last line
   * against a soft lock — a fully drained room keeps 40% of its ambient, plus
   * the sliver every fixture holds back, plus the torch, plus the lens light
   * that is never drained at all.
   */
  private applyDrain(dt: number): void {
    const rig = this.lightRig;
    // Deliberately not an early-out on a level with the effect off: this has to
    // keep running so that a descent from a drained corridor into Lights Out
    // converges the torch and the ambient back rather than stranding them.
    this.drain = damp(this.drain, rig && rig.drainEnabled ? rig.localDrain : 0, 2.5, dt);
    if (this.drain < 0.001) this.drain = 0;
    const k = 1 - this.drain * TUNING.render.lightDrainAmbientDepth;
    this.ambient.intensity = this.ambientBase * k;
    this.bounce.intensity = this.bounceBase * k;
    this.flashlight.setDrain(this.drain);
    this.postfx?.setDrain(this.drain);
  }

  /** Two grid samples back along the ray. Not shadowing, just "is there a wall". */
  private blocked(x: number, z: number, dx: number, dz: number): boolean {
    const g = this.grid;
    if (!g) return false;
    return g.isSolidAt(x - dx * 0.5, z - dz * 0.5) || g.isSolidAt(x - dx * 0.82, z - dz * 0.82);
  }

  private dynamicLightBudget(): number {
    const max = TUNING.render.maxDynamicLights;
    return this.settings.quality === 'low' ? Math.max(3, Math.ceil(max / 2)) : max;
  }

  /**
   * Distance first, frustum second. The distance test uses the chunk's box
   * rather than its centre so a chunk is never dropped while the player is
   * standing at its far edge.
   */
  private cullChunks(): void {
    if (!this.geometry) return;
    this.projScreen.multiplyMatrices(this.camera.projectionMatrix, this.camera.matrixWorldInverse);
    this.frustum.setFromProjectionMatrix(this.projScreen);

    const cull = this.cullDistance;
    const cull2 = cull * cull;
    // In a session the camera is a child of the rig, so its `position` is the
    // head's offset inside the play space rather than a point in the level.
    // The flat path keeps reading `position` directly, unchanged.
    let px = this.camera.position.x;
    let pz = this.camera.position.z;
    if (this.xrOn) {
      this.camWorld.setFromMatrixPosition(this.camera.matrixWorld);
      px = this.camWorld.x;
      pz = this.camWorld.z;
    }
    for (const chunk of this.geometry.chunks) {
      const dx = Math.max(0, Math.abs(px - chunk.centerX) - chunk.halfX);
      const dz = Math.max(0, Math.abs(pz - chunk.centerZ) - chunk.halfZ);
      chunk.group.visible = dx * dx + dz * dz <= cull2 && this.frustum.intersectsBox(chunk.box);
    }
    this.cullProps(px, pz);
  }

  /**
   * Scenery keeps the ordinary cull radius even where the building does not.
   *
   * A level with a long view is paying for the long view, and a hedge at eighty
   * metres is four pixels of it. The exception is anything tall enough to be a
   * landmark: those are the reason the view was lengthened, and they are a
   * handful per level. Below the boundary three.js frustum-culls them as usual.
   */
  private cullProps(px: number, pz: number): void {
    const near2 = TUNING.render.cullDistance * TUNING.render.cullDistance;
    for (let i = 0; i < this.propViews.length; i++) {
      const a = this.propAnchors[i]!;
      if (a.landmark) continue;
      const dx = px - a.x;
      const dz = pz - a.z;
      this.propViews[i]!.object.visible = dx * dx + dz * dz <= near2;
    }
  }
}

/**
 * Metres of authored height at which a prop stops being scenery and starts
 * being something the player navigates by. Only the water tower reaches it.
 */
const LANDMARK_HEIGHT = 6;

/**
 * `RenderAPI` plus the immersive session.
 *
 * `RenderAPI` in `core/types.ts` is the contract every system reads the
 * renderer through and it does not know about headsets — nor should it, since
 * nothing outside `game.ts` has any business with one. This is the wider type
 * the integration seam holds, and it is a structural superset, so
 * `GameContext.render` still takes it without a cast.
 */
export interface GameRenderer extends RenderAPI {
  /** The immersive session. Inert on a machine with no headset; see `xr.ts`. */
  readonly xr: XRRuntime;
  /**
   * The camera with a world-space `position` and `quaternion`, for the two
   * multiplayer views that aim off those fields rather than off `matrixWorld`.
   * The very same object as `camera` on every flat frame.
   */
  aimCamera(): THREE.Camera;
}

/** Build the renderer. `game.ts` calls this once at boot. */
export function makeRenderer(canvas: HTMLCanvasElement, settings: RenderSettings): GameRenderer {
  return new Renderer(canvas, settings);
}
